import { RosterColumn, rosterColumnsFor } from '../../services/roster/roster.columns';
import type { RosterDataSource, RosterRecord, RosterRole, RosterTab } from '../../services/roster/roster.types';
import { RosterStore } from '../../stores/roster/roster.store';
import { FilterMethod, ICustomFilter } from '../data-table/components/filters/filters.models';
import { DataTablePresenter, ITableColumn } from '../data-table/data-table.presenter';
import { rosterOpenColumn } from './roster-actions';
import { rosterDisplayText } from './roster-display';
import React from 'react';
import { CellProps } from 'react-table';
import * as S from './roster-table.styles';

/** Widths in px — the table's flex layout otherwise gives every column 100. */
const WIDTHS: Record<string, number> = {
    name: 220,
    type: 130,
    npi: 130,
    organization: 220,
    tags: 170,
    updated: 120,
    email: 240,
    taxonomyCode: 130,
    ein: 120,
    ptan: 110,
    license: 140,
    automation: 250,
    halt: 100,
    id: 170,
    placeOfService: 200,
    address: 320,
    nickname: 160,
    other: 120,
};

/**
 * One table presenter per tab and role. Each keeps its own sort, page size, hidden columns and
 * selection — and persists them under its own name, so a client admin's column choices can never
 * surface an ops-only column in an ops session or the other way round.
 */
export function createRosterPresenter(
    tab: RosterTab,
    role: RosterRole,
    dataSource: RosterDataSource,
    openRecord: (id: string) => void,
): DataTablePresenter<RosterRecord> {
    return new DataTablePresenter<RosterRecord>({
        name: `roster_${tab}_${role}_${dataSource.kind}`,
        data: [],
        columns: [
            ...rosterColumnsFor(tab, role, dataSource.capabilities).map(column =>
                toTableColumn(tab, column, dataSource.canSort(tab, column.key)),
            ),
            // The design's trailing chevron.
            rosterOpenColumn(openRecord),
        ],
        filterSchemas: [],
        filters: [],
    });
}

/** `sortable` is the source's to say: the API orders by the fields it indexes, not by every column. */
function toTableColumn(tab: RosterTab, column: RosterColumn, sortable: boolean): ITableColumn<RosterRecord> {
    return {
        id: column.key,
        Header: column.label,
        accessor: (record: RosterRecord): string => rosterDisplayText(tab, record, column.key),
        width: WIDTHS[column.key] ?? 150,
        // Hidden until the user asks for it; the picker's choice is then persisted by the presenter.
        show: column.defaultVisible === true,
        defaultSort: column.key === 'name' ? { id: 'name', desc: false } : undefined,
        disableSortBy: !sortable,
        Cell: ({ value }: CellProps<RosterRecord, string>): React.ReactElement => renderCell(column, value),
    };
}

function renderCell(column: RosterColumn, text: string): React.ReactElement {
    if (!text) {
        return <S.Absent>—</S.Absent>;
    }
    switch (column.cell) {
        case 'link':
            return <S.LinkText>{text}</S.LinkText>;
        case 'tag':
            return <S.Chip tone={column.key === 'halt' ? 'warning' : 'primary'}>{text}</S.Chip>;
        case 'num':
            return <S.NumText>{text}</S.NumText>;
        default:
            return <S.PlainText>{text}</S.PlainText>;
    }
}

/**
 * The store's search and type, as table filters.
 *
 * The table resets to its first page and asks for data whenever its valid filters change, so
 * mirroring the store's filters here is what makes a new search start from page one. The server
 * never sees these ids — `RosterStore.loadPage` builds the query from its own fields. `locked`
 * keeps them out of reach of the table's own filter chips.
 */
export function rosterTableFilters(store: RosterStore): ICustomFilter[] {
    const filters: ICustomFilter[] = [];
    const search = store.search.trim();
    if (search) {
        filters.push({
            filterId: 'rosterSearch',
            filterMethod: FilterMethod.LK,
            filterType: 'String',
            value: search,
            locked: true,
        });
    }
    if (store.tab === 'providers' && store.providerType) {
        filters.push({
            filterId: 'rosterProviderType',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: store.providerType,
            locked: true,
        });
    }
    return filters;
}

/** The columns the user has visible, in table order — what the CSV exports. */
export function rosterVisibleColumns(
    presenter: DataTablePresenter<RosterRecord>,
    tab: RosterTab,
    role: RosterRole,
    dataSource: RosterDataSource,
): RosterColumn[] {
    return rosterColumnsFor(tab, role, dataSource.capabilities).filter(
        column => !presenter.hiddenColumnIds.includes(column.key),
    );
}
